import { Injectable, signal } from '@angular/core';
import { BaseService } from './base.service';
import {
  catchError,
  lastValueFrom,
  map,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import { ActionType } from '../config/action.config';
import { ProjectConfig } from '../interfaces/project-config.interface';
import { ValidateAccess } from '../auth/interfaces/validate-access.interface';
import { environment } from '../../../environments/environment';
import { KeyCloakInfo } from '@muvstok/ngx-muvstok';

@Injectable({ providedIn: 'root' })
export class ConfigService extends BaseService {
  private readonly _config = signal<ProjectConfig | null>(null);
  private readonly _validActions = signal<ActionType[] | null>(null);

  get validActions() {
    const value = this._validActions();
    if (value === null) throw 'Nenhuma permissão não carregada.';

    return value;
  }

  set validActions(value: ActionType[]) {
    this._validActions.set(value);
  }

  get config(): ProjectConfig {
    const value = this._config();
    if (value === null) throw 'Configuração por projeto não carregada.';

    return value;
  }

  load() {
    return lastValueFrom(
      this.http
        .get<ProjectConfig>(`${this.baseUrlMuvStok}/projects/GetConfigByHost`)
        .pipe(
          switchMap((item) => {
            this._config.set(item);
            return of({
              clientId: item.clientId,
              url: environment.identityURL,
              realm: environment.realm,
            } as KeyCloakInfo);
          }),
          catchError((error) => {
            if (error.status === 503) {
              this.router.navigateByUrl('/unavailable');
              return of({} as KeyCloakInfo);
            } else {
              throw error;
            }
          }),
        ),
    );
  }

  loadActions(): Promise<boolean> {
    return lastValueFrom(
      this.http
        .post<ValidateAccess>(
          `${this.baseUrlMuvStok}/auth/validateAccessAutomaker`,
          {},
        )
        .pipe(
          map((item) => {
            this._validActions.set(item.validActions);
            if (!item.valid) {
              this.router.navigate(['/no-access']);
            }
            return item.valid;
          }),
          catchError((error: any) => {
            if (error.status === 401) {
              this._validActions.set([]);
              return of(true);
            }

            return throwError(() => error);
          }),
        ),
    );
  }
}
